import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useAuth } from '../../contexts/AuthContext';
import { Box, Spinner, Center, Text, VStack, Button } from '@chakra-ui/react';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean; // true = 認証必須, false = 認証不要, undefined = どちらでも可
  redirectTo?: string;
  fallback?: React.ReactNode;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requireAuth = true,
  redirectTo,
  fallback
}) => {
  const { user, loading, authenticated, checkUserStatus, loggingOut, connectionError, retryAuth, logout } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (loading) {
      return; // ローディング中は何もしない
    }

    // 認証必須ページで「メール未認証」を弾く
    if (requireAuth && authenticated && user) {
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      const allowUnverified = ['/email-verification', '/logout'];
      const isAllowed = allowUnverified.some((p) => path.startsWith(p));

      const ensureVerified = async () => {
        // まずローカルのユーザー情報を確認し、未認証ならAPIで再確認
        let isVerified = Boolean((user as any).email_verified_at);
        if (!isVerified) {
          try {
            const status = await checkUserStatus(user.email);
            isVerified = !!status.email_verified;
          } catch (_) {
            // 失敗時は未認証扱い
            isVerified = false;
          }
        }

        if (!isVerified && !isAllowed) {
          router.push(`/email-verification?email=${encodeURIComponent(user.email)}`);
          return;
        }
      };

      void ensureVerified();
    }

    // 通信の失敗でログイン状態を確かめられなかったときは、ログイン画面へ飛ばさない（ログイン情報は残っている）。
    // 下の「通信できません」画面で、もう一度読み込ませる（2026-10-01・UX 監査 K-2）
    if (requireAuth && !authenticated && connectionError) {
      return;
    }

    // 認証が必要なページで未認証の場合
    if (requireAuth && !authenticated) {
      const loginUrl = redirectTo || '/login';
      const currentPath = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';

      // 現在のパスをクエリパラメータとして保存
      if (currentPath !== '/login') {
        router.push(`${loginUrl}?redirect=${encodeURIComponent(currentPath)}`);
      } else {
        router.push(loginUrl);
      }
      return;
    }

  // 認証不要なページで認証済みの場合（例：ログインページ）。
  // ただし、/forgot-password と /email-verification は認証済みでも閲覧を許可する。
    if (requireAuth === false && authenticated) {
      const dashboardUrl = redirectTo || '/admin/dashboard';
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      const allowWhileAuthenticated = ['/forgot-password', '/email-verification'];
      const allowed = allowWhileAuthenticated.some((p) => path.startsWith(p));
      if (!allowed) {
        router.push(dashboardUrl);
        return;
      }
    }
  }, [loading, authenticated, requireAuth, redirectTo, router, connectionError]);

  // ログアウト中の表示（全画面）
  if (loggingOut) {
    return (
      <Center minH="100vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color="gray.600" fontSize="lg">ログアウトしています…</Text>
        </VStack>
      </Center>
    );
  }

  // ローディング中の表示
  if (loading) {
    return fallback || (
      <Center minH="100vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color="gray.600" fontSize="lg">認証状態を確認中...</Text>
        </VStack>
      </Center>
    );
  }

  // 通信できずにログイン状態を確かめられなかった（電波が弱い地下の店など）。ログアウトはしていない
  if (requireAuth && !authenticated && connectionError) {
    return (
      <Center minH="100vh" px={6}>
        <VStack spacing={4} maxW="sm" textAlign="center">
          <Text fontSize="xl" fontWeight="bold">通信できませんでした</Text>
          <Text color="gray.600">
            電波（Wi-Fi）を確かめて、もう一度読み込んでください。ログインはそのままです。
          </Text>
          <Button colorScheme="blue" size="lg" w="full" onClick={() => { void retryAuth(); }}>
            もう一度読み込む
          </Button>
          {/* 退路。サーバ側の失敗が続くときなど、何度読み込んでも直らない場合だけ使う（押すとログインし直しになるので目立たせない） */}
          <Button variant="link" size="sm" color="gray.500" onClick={() => { void logout(); }}>
            電波の届く場所でも直らないときは、ログインし直す
          </Button>
        </VStack>
      </Center>
    );
  }

  // 認証が必要で未認証の場合は何も表示しない（リダイレクト処理中）
  if (requireAuth && !authenticated) {
    return fallback || (
      <Center minH="100vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color="gray.600" fontSize="lg">ログインページに移動中...</Text>
        </VStack>
      </Center>
    );
  }

  // 認証不要で認証済みの場合も何も表示しない（リダイレクト処理中）
  if (requireAuth === false && authenticated) {
    return fallback || (
      <Center minH="100vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color="gray.600" fontSize="lg">ダッシュボードに移動中...</Text>
        </VStack>
      </Center>
    );
  }

  // 条件を満たしている場合は子コンポーネントを表示
  return <>{children}</>;
};

// よく使われるパターンのためのヘルパーコンポーネント
export const RequireAuth: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode; redirectTo?: string }> = ({
  children,
  fallback,
  redirectTo
}) => (
  <AuthGuard requireAuth={true} fallback={fallback} redirectTo={redirectTo}>
    {children}
  </AuthGuard>
);

export const RequireGuest: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode; redirectTo?: string }> = ({
  children,
  fallback,
  redirectTo
}) => (
  <AuthGuard requireAuth={false} fallback={fallback} redirectTo={redirectTo}>
    {children}
  </AuthGuard>
);

export const OptionalAuth: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode; redirectTo?: string }> = ({
  children,
  fallback,
  redirectTo
}) => (
  <AuthGuard requireAuth={undefined} fallback={fallback} redirectTo={redirectTo}>
    {children}
  </AuthGuard>
);